import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay, faPause, faCamera, faUpload, faFlagCheckered, faFlag, faXmark } from "@fortawesome/free-solid-svg-icons";

const CLOUDINARY_CLOUD = "dxtj7sllk";
const CLOUDINARY_PRESET = "d5rr7y5s";

const fmtTime = (s) => {
  if (!Number.isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
};

// Éditeur vidéo léger, 100% navigateur : lecture, scrub précis, et capture de
// deux frames exactes (début / fin, la fin étant optionnelle) à l'endroit de
// la tête de lecture — Veo 3.1 interpole entre ces deux keyframes. L'API ne
// prend pas de vidéo entière en entrée, d'où ce passage par des frames.
const VideoEditorPanel = ({ initialUrl, onStartFrameCaptured, onEndFrameCaptured, startFrameUrl, endFrameUrl }) => {
  const videoRef = useRef(null);
  const [src, setSrc] = useState(initialUrl || null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [inPoint, setInPoint] = useState(0);
  const [outPoint, setOutPoint] = useState(0);
  const [capturing, setCapturing] = useState(null); // 'start' | 'end' | null

  useEffect(() => { if (initialUrl) setSrc(initialUrl); }, [initialUrl]);

  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setSrc(url);
    setPlaying(false);
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };

  const onLoadedMetadata = () => {
    const v = videoRef.current;
    if (!v) return;
    setDuration(v.duration || 0);
    setOutPoint(v.duration || 0);
  };

  const onTimeUpdate = () => setCurrentTime(videoRef.current?.currentTime || 0);

  const seek = (t) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = t;
    setCurrentTime(t);
  };

  // Capture la frame à la tête de lecture et la place comme keyframe début ou fin.
  const captureFrame = async (target) => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return toast.warn("Chargez une vidéo d'abord.");
    setCapturing(target);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = v.videoWidth;
      canvas.height = v.videoHeight;
      canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
      if (!blob) throw new Error("capture impossible");
      const fd = new FormData();
      fd.append("file", blob);
      fd.append("upload_preset", CLOUDINARY_PRESET);
      const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/image/upload`, { method: "POST", body: fd });
      const data = await res.json();
      if (!data.secure_url) throw new Error(data?.error?.message || "upload échoué");
      if (target === "start") { onStartFrameCaptured?.(data.secure_url); setInPoint(currentTime); }
      else { onEndFrameCaptured?.(data.secure_url); setOutPoint(currentTime); }
      toast.success(`Frame ${target === "start" ? "début" : "fin"} capturée à ${fmtTime(currentTime)}`);
    } catch (err) {
      toast.error(err.message || "Capture de frame impossible.");
    } finally { setCapturing(null); }
  };

  const pct = (t) => (duration > 0 ? (t / duration) * 100 : 0);

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-bold text-[#221C51]">Éditeur vidéo</p>
        <label className="flex items-center gap-1.5 text-xs font-semibold text-fuchsia-700 bg-fuchsia-50 px-2.5 py-1.5 rounded-full cursor-pointer hover:bg-fuchsia-100">
          <FontAwesomeIcon icon={faUpload} />
          Importer une vidéo
          <input type="file" accept="video/*" className="hidden" onChange={handleImport} />
        </label>
      </div>

      {/* Frames déjà choisies (capturées ici, ou rechargées depuis l'historique) —
          visibles même sans vidéo importée, puisqu'elles suffisent à générer. */}
      {(startFrameUrl || endFrameUrl) && (
        <div className="flex items-center gap-4 mb-3">
          <FrameThumb label="Début" url={startFrameUrl} onClear={() => onStartFrameCaptured?.(null)} required />
          <FrameThumb label="Fin (optionnel)" url={endFrameUrl} onClear={() => onEndFrameCaptured?.(null)} />
        </div>
      )}

      {!src ? (
        <div className="aspect-video rounded-xl bg-gray-50 border border-dashed border-gray-300 flex items-center justify-center text-sm text-gray-400">
          Importez une vidéo ou générez-en une pour capturer de nouvelles frames.
        </div>
      ) : (
        <>
          <div className="relative rounded-xl overflow-hidden bg-black">
            <video
              ref={videoRef}
              src={src}
              className="w-full aspect-video object-contain bg-black"
              onLoadedMetadata={onLoadedMetadata}
              onTimeUpdate={onTimeUpdate}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
            />
            <button onClick={togglePlay} className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/20 transition group">
              <span className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                <FontAwesomeIcon icon={playing ? faPause : faPlay} className="text-[#221C51]" />
              </span>
            </button>
          </div>

          {/* Timeline — repères début (flag) / fin (flag damier) posés lors des captures */}
          <div className="mt-3">
            <div className="relative h-2 bg-gray-100 rounded-full">
              <div className="absolute inset-y-0 bg-fuchsia-200 rounded-full" style={{ left: `${pct(inPoint)}%`, right: `${100 - pct(outPoint)}%` }} />
              <div className="absolute inset-y-0 left-0 bg-[#221C51] rounded-full" style={{ width: `${pct(currentTime)}%` }} />
              <input
                type="range" min={0} max={duration || 0} step={0.01} value={currentTime}
                onChange={(e) => seek(Number(e.target.value))}
                className="absolute inset-0 w-full opacity-0 cursor-pointer"
              />
              <div className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-[#221C51] border-2 border-white shadow" style={{ left: `calc(${pct(currentTime)}% - 6px)` }} />
            </div>
            <div className="flex items-center justify-between text-[11px] text-gray-400 mt-1.5">
              <span>{fmtTime(currentTime)}</span>
              <span>{fmtTime(duration)}</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-3">
            <button onClick={() => captureFrame("start")} disabled={capturing !== null}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#221C51] text-white hover:bg-[#2e265e] disabled:opacity-50">
              <FontAwesomeIcon icon={faFlag} className="text-[10px]" />
              {capturing === "start" ? "Capture…" : "Capturer → Début"}
            </button>
            <button onClick={() => captureFrame("end")} disabled={capturing !== null}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-fuchsia-600 text-white hover:bg-fuchsia-700 disabled:opacity-50">
              <FontAwesomeIcon icon={faFlagCheckered} className="text-[10px]" />
              {capturing === "end" ? "Capture…" : "Capturer → Fin (optionnel)"}
            </button>
            <span className="flex items-center gap-1 text-[11px] text-gray-400 ml-auto">
              <FontAwesomeIcon icon={faCamera} /> à {fmtTime(currentTime)}
            </span>
          </div>
        </>
      )}
    </div>
  );
};

const FrameThumb = ({ label, url, onClear, required }) => (
  <div className="flex items-center gap-2">
    {url ? (
      <div className="relative">
        <img src={url} alt={label} className="w-14 h-14 rounded-lg object-cover border border-gray-200" />
        {!required && (
          <button onClick={onClear} className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-gray-700 text-white flex items-center justify-center">
            <FontAwesomeIcon icon={faXmark} className="text-[8px]" />
          </button>
        )}
      </div>
    ) : (
      <div className="w-14 h-14 rounded-lg border border-dashed border-gray-300 flex items-center justify-center text-gray-300 text-[10px] text-center px-1">vide</div>
    )}
    <span className="text-[11px] text-gray-500 font-medium">{label}</span>
  </div>
);

export default VideoEditorPanel;
